import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Mail,
  Phone,
  Building2,
  Calendar,
  Package,
  Users,
  ShoppingCart,
  CreditCard,
  Ban,
  Trash2,
  RefreshCw,
  CheckCircle2,
  Copy,
  ExternalLink
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SuperAdminRestaurantDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState({ open: false, type: null, reason: '' });
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [resetDialog, setResetDialog] = useState({ open: false, link: null });

  useEffect(() => {
    fetchRestaurantDetail();
  }, [id]);

  const fetchRestaurantDetail = async () => {
    const token = localStorage.getItem('superadmin_token');
    if (!token) {
      navigate('/orderlab-admin/login');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/superadmin/restaurants/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else if (response.status === 401) {
        navigate('/orderlab-admin/login');
      }
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action) => {
    const token = localStorage.getItem('superadmin_token');
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/superadmin/restaurants/${id}/action?action=${action}${actionDialog.reason ? `&reason=${encodeURIComponent(actionDialog.reason)}` : ''}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const result = await response.json();
        
        if (action === 'reset_password') {
          setResetDialog({ open: true, link: result.reset_link });
        } else {
          toast.success(result.message);
          if (action === 'delete') {
            navigate('/orderlab-admin/restaurants');
          } else {
            fetchRestaurantDetail();
          }
        }
      } else {
        toast.error('Action échouée');
      }
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setActionDialog({ open: false, type: null, reason: '' });
      setDeleteDialog(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papier');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Restaurant non trouvé</p>
      </div>
    );
  }

  const { restaurant, stats, last_login, last_order } = data;
  const isSuspended = restaurant.suspended;

  const getPlanColor = (plan) => {
    const colors = {
      free: 'bg-slate-50 text-slate-700 border-slate-200',
      pro: 'bg-blue-50 text-blue-700 border-blue-200',
      plus: 'bg-purple-50 text-purple-700 border-purple-200'
    };
    return colors[plan] || colors.free;
  };

  const StatCard = ({ icon: Icon, label, value, subtitle }) => (
    <Card className="p-6 hover:shadow-md transition-shadow" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="p-3 rounded-lg bg-primary/10">
          <Icon className="w-5 h-5 text-primary" />
        </div>
      </div>
      <div>
        <p className="text-sm text-muted-foreground mb-1">{label}</p>
        <p className="text-2xl font-semibold tabular-nums">{value}</p>
        {subtitle && <p className="text-xs text-muted-foreground mt-2">{subtitle}</p>}
      </div>
    </Card>
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/orderlab-admin/restaurants')}
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-semibold">{restaurant.name}</h1>
              <Badge className={`${getPlanColor(restaurant.plan)} border`} data-testid="plan-badge">
                {restaurant.plan.toUpperCase()}
              </Badge>
              {isSuspended && (
                <Badge variant="destructive" className="flex items-center gap-1" data-testid="suspended-badge">
                  <Ban className="w-3 h-3" />
                  SUSPENDU
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                {restaurant.email}
              </span>
              {restaurant.phone && (
                <span className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  {restaurant.phone}
                </span>
              )}
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Créé le {new Date(restaurant.created_at).toLocaleDateString('fr-FR')}
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setActionDialog({ open: true, type: 'reset_password', reason: '' })}
            data-testid="reset-password-button"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Reset MDP
          </Button>
          {isSuspended ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setActionDialog({ open: true, type: 'unsuspend', reason: '' })}
              data-testid="unsuspend-button"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Réactiver
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setActionDialog({ open: true, type: 'suspend', reason: '' })}
              data-testid="suspend-button"
            >
              <Ban className="w-4 h-4 mr-2" />
              Suspendre
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDeleteDialog(true)}
            className="text-destructive hover:text-destructive"
            data-testid="delete-button"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Supprimer
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={ShoppingCart}
          label="Commandes ce mois"
          value={stats.orders_created_this_month}
          subtitle={`${stats.orders_sent_this_month} envoyées`}
        />
        <StatCard
          icon={Package}
          label="Produits"
          value={stats.products_count}
        />
        <StatCard
          icon={Building2}
          label="Fournisseurs"
          value={stats.suppliers_count}
        />
        <StatCard
          icon={Users}
          label="Utilisateurs"
          value={stats.users_count}
        />
      </div>

      {/* Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Activity */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">📈 Activité</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
              <span className="text-sm font-medium">Total commandes</span>
              <span className="text-lg font-semibold tabular-nums">{stats.total_orders}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
              <span className="text-sm font-medium">Dernière commande</span>
              <span className="text-sm text-muted-foreground">
                {last_order ? new Date(last_order.created_at).toLocaleDateString('fr-FR') : 'Aucune'}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
              <span className="text-sm font-medium">Dernière connexion</span>
              <span className="text-sm text-muted-foreground">
                {last_login ? new Date(last_login.last_login_at).toLocaleDateString('fr-FR') : 'Aucune'}
              </span>
            </div>
          </div>
        </Card>

        {/* Billing */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">💳 Facturation</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
              <span className="text-sm font-medium">Plan</span>
              <Badge className={getPlanColor(restaurant.plan)}>{restaurant.plan.toUpperCase()}</Badge>
            </div>
            <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
              <span className="text-sm font-medium">Statut Stripe</span>
              <Badge variant={restaurant.subscription_status === 'active' ? 'default' : 'destructive'}>
                {restaurant.subscription_status || 'N/A'}
              </Badge>
            </div>
            {restaurant.stripe_subscription_id && (
              <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                <span className="text-sm font-medium">ID Subscription</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(restaurant.stripe_subscription_id)}
                  className="h-auto p-1"
                >
                  <span className="text-xs font-mono">{restaurant.stripe_subscription_id.substring(0, 15)}...</span>
                  <Copy className="w-3 h-3 ml-2" />
                </Button>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Dialogs */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => setActionDialog({ ...actionDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionDialog.type === 'suspend' && 'Suspendre le restaurant'}
              {actionDialog.type === 'unsuspend' && 'Réactiver le restaurant'}
              {actionDialog.type === 'reset_password' && 'Réinitialiser le mot de passe'}
            </DialogTitle>
            <DialogDescription>
              {actionDialog.type === 'suspend' && 'Le restaurant ne pourra plus se connecter.'}
              {actionDialog.type === 'unsuspend' && 'Le restaurant pourra à nouveau se connecter.'}
              {actionDialog.type === 'reset_password' && 'Un lien de réinitialisation sera généré (valable 24h).'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="reason">Raison (optionnelle)</Label>
              <Textarea
                id="reason"
                placeholder="Pourquoi cette action est nécessaire..."
                value={actionDialog.reason}
                onChange={(e) => setActionDialog({ ...actionDialog, reason: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog({ open: false, type: null, reason: '' })}>
              Annuler
            </Button>
            <Button onClick={() => handleAction(actionDialog.type)} data-testid="confirm-action">
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>⚠️ Supprimer définitivement ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Toutes les données seront supprimées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleAction('delete')}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete"
            >
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={resetDialog.open} onOpenChange={(open) => setResetDialog({ ...resetDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>🔗 Lien de réinitialisation généré</DialogTitle>
            <DialogDescription>
              Envoyez ce lien au client. Il est valable 24 heures.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-muted/50 rounded-lg border">
              <p className="text-sm font-mono break-all">{resetDialog.link}</p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => copyToClipboard(resetDialog.link)}
                data-testid="copy-reset-link"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copier le lien
              </Button>
              <Button
                variant="outline"
                onClick={() => window.open(resetDialog.link, '_blank')}
              >
                <ExternalLink className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setResetDialog({ open: false, link: null })}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
